import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Coins, Loader2, Calendar, TrendingUp, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { formatNumber } from "@/lib/web3";
import { useAuth } from "@/contexts/AuthContext";

function PlanCard({ plan, onStake }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className="umakant-card p-5 sm:p-6 flex flex-col"
      data-testid={`stake-plan-${plan.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-lg font-semibold">{plan.name}</div>
        <Badge className="bg-[rgba(124,58,237,0.16)] text-[var(--purple-300)] border border-[rgba(124,58,237,0.35)] hover:bg-[rgba(124,58,237,0.16)]">
          {plan.apy}% APY
        </Badge>
      </div>
      <div className="flex items-center gap-2 text-xs text-[var(--text-3)] mb-4">
        <Calendar size={14} /> {plan.duration_days} days lock
      </div>
      <div className="space-y-1.5 text-xs text-[var(--text-3)] mb-5">
        <div className="flex items-center justify-between">
          <span>Min</span><span className="mono text-[var(--text-2)]">{formatNumber(plan.min_amount)} USDT</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Max</span><span className="mono text-[var(--text-2)]">{formatNumber(plan.max_amount)} USDT</span>
        </div>
      </div>
      <Button
        data-testid={`stake-select-${plan.id}`}
        onClick={() => onStake(plan)}
        className="mt-auto text-white hover:opacity-90 font-semibold" style={{ background: "var(--gradient-primary)" }}
      >
        Stake now
      </Button>
    </motion.div>
  );
}

export default function Staking() {
  const [plans, setPlans] = useState([]);
  const [stakes, setStakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const { refreshUser } = useAuth();

  const load = async () => {
    try {
      const [p, m] = await Promise.all([api.get("/stake/plans"), api.get("/stake/my")]);
      setPlans(p.data.plans || []);
      setStakes(m.data.stakes || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSelect = (plan) => {
    setSelected(plan);
    setAmount("");
    setOpen(true);
  };

  const submit = async () => {
    if (!selected) return;
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setSubmitting(true);
    try {
      await api.post("/stake/create", { plan_id: selected.id, amount: amt });
      toast.success(`Staked ${amt} USDT in ${selected.name}`);
      setOpen(false);
      await load();
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stake failed");
    } finally {
      setSubmitting(false);
    }
  };

  const claim = async (stake) => {
    try {
      const r = await api.post(`/stake/claim/${stake.id}`);
      toast.success(`Claimed ${formatNumber(r.data.claimed)} USDT`);
      await load();
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed");
    }
  };

  const unstake = async (stake) => {
    try {
      await api.post(`/stake/unstake/${stake.id}`);
      toast.success("Unstaked");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unstake failed");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold">Staking</h1>
        <p className="text-sm text-[var(--text-3)] mt-1">Stake USDT to earn rewards and grow your team commissions.</p>
      </div>

      <Tabs defaultValue="plans" className="space-y-4">
        <TabsList className="bg-[var(--surface-1)] border border-white/5">
          <TabsTrigger value="plans" data-testid="staking-tab-plans">Plans</TabsTrigger>
          <TabsTrigger value="active" data-testid="staking-tab-active">My stakes</TabsTrigger>
        </TabsList>

        <TabsContent value="plans">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1,2,3,4].map((i) => <Skeleton key={i} className="h-48 bg-white/5" />)}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans.map((p) => <PlanCard key={p.id} plan={p} onStake={handleSelect} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="active">
          <div className="umakant-card overflow-hidden">
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">
                  <tr className="border-b border-white/5">
                    <th className="text-left px-5 py-3">Plan</th>
                    <th className="text-right px-5 py-3">Amount</th>
                    <th className="text-right px-5 py-3">APY</th>
                    <th className="text-right px-5 py-3">Earned</th>
                    <th className="text-left px-5 py-3">Status</th>
                    <th className="text-right px-5 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="my-stakes-table">
                  {stakes.length === 0 ? (
                    <tr><td colSpan={6} className="text-center px-5 py-12 text-[var(--text-3)]">No stakes yet. Choose a plan to begin.</td></tr>
                  ) : stakes.map((s) => (
                    <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-5 py-3">
                        <div className="font-medium">{s.plan_name}</div>
                        <div className="text-xs text-[var(--text-3)]">{s.duration_days} days</div>
                      </td>
                      <td className="px-5 py-3 text-right mono">{formatNumber(s.amount)}</td>
                      <td className="px-5 py-3 text-right mono">{s.apy}%</td>
                      <td className="px-5 py-3 text-right mono text-[var(--purple-300)]">{formatNumber(s.earned)}</td>
                      <td className="px-5 py-3">
                        <Badge className={s.status === "active"
                          ? "bg-[rgba(34,197,94,0.14)] text-[#86EFAC] border border-[rgba(34,197,94,0.22)] hover:bg-[rgba(34,197,94,0.14)]"
                          : "bg-[var(--surface-1)] text-[var(--text-3)] border border-white/10 hover:bg-[var(--surface-1)]"}>
                          {s.status}
                        </Badge>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <Button data-testid={`stake-claim-${s.id}`} onClick={() => claim(s)} size="sm" disabled={s.status !== "active"} className="text-white hover:opacity-90" style={{ background: "var(--gradient-primary)" }}>Claim</Button>
                          <Button data-testid={`stake-unstake-${s.id}`} onClick={() => unstake(s)} size="sm" variant="secondary" disabled={s.status !== "active"} className="bg-[var(--surface-1)] border border-white/10">Unstake</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--surface-2)] border border-white/10 text-[var(--text-1)] sm:max-w-md" data-testid="stake-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Stake in {selected?.name}</DialogTitle>
            <DialogDescription className="text-[var(--text-3)]">
              {selected?.duration_days} days lock · {selected?.apy}% APY · USDT
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-[var(--text-3)] uppercase tracking-[0.14em] mb-2 block">Amount (USDT)</label>
              <div className="relative">
                <Input
                  data-testid="staking-amount-input"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`Min ${selected?.min_amount} - Max ${selected?.max_amount}`}
                  type="number"
                  className="bg-[var(--surface-1)] border-white/10 mono pr-16 h-11"
                />
                <button
                  type="button"
                  onClick={() => setAmount(String(selected?.min_amount || ""))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs px-2 py-1 rounded bg-[var(--surface-3)] text-[var(--text-2)] hover:text-[var(--text-1)]"
                >MIN</button>
              </div>
            </div>
            <div className="umakant-panel p-3 text-xs text-[var(--text-3)]">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 size={14} className="text-[var(--purple-300)]" />
                Off-chain ledger MVP: your stake is recorded immediately. On-chain mode will be enabled once the contract is deployed.
              </div>
            </div>
            <Button
              data-testid="staking-submit-button"
              onClick={submit}
              disabled={submitting}
              className="w-full text-white hover:opacity-90 font-semibold h-11" style={{ background: "var(--gradient-primary)" }}
            >
              {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Coins size={16} className="mr-2" />}
              Confirm stake
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
